import Build from '../core/Build';
import { withSolverMaging } from '../core/itemMaging';
import { SLOT_IDX } from '../core/slotIdx';
import { ATTRIBUTES, investedPointsToStatValue } from '../core/st';

const DEFAULT_LVL = 200;

function weaponIdOf(items) {
    const entry = Array.isArray(items) ? items[SLOT_IDX.WEAPON] : null;
    const id = typeof entry === 'number' ? entry : Number(entry?.id);
    return Number.isFinite(id) && id > 0 ? String(id) : '';
}

function attributesFromInvestedPoints(investedPoints, lvl = DEFAULT_LVL) {
    const attributes = {};
    if (!Array.isArray(investedPoints)) return attributes;
    ATTRIBUTES.forEach((id, idx) => {
        const points = Number(investedPoints[idx]) || 0;
        attributes[id] = investedPointsToStatValue(lvl, id, points);
    });
    return attributes;
}

export function applySolverBuildPayload({
    solverBuildPayload,
    baseBuild,
}) {
    if (!solverBuildPayload || !baseBuild) return null;

    const attributes = attributesFromInvestedPoints(
        solverBuildPayload.invested_points,
        baseBuild?.lvl || DEFAULT_LVL,
    );

    // Scrolls are a request setting the user owns, not something the solver
    // hands back: a serialized solver build carries items, invested points and
    // set bonuses only. Without carrying the base build's scrolls over,
    // `hasScrolls` lands here as `undefined` and Build falls back to its
    // +100-per-characteristic default — so a scroll toggle switched off flips
    // back on the moment a generation finishes.
    const payloadHasScrolls = typeof solverBuildPayload.hasScrolls === 'boolean'
        ? solverBuildPayload.hasScrolls
        : null;
    const scrolls = (payloadHasScrolls === null && baseBuild?.scrolls)
        ? { ...baseBuild.scrolls }
        : {};

    // A forgemage belongs to the weapon it was chosen for, so a run that swaps
    // the weapon drops both halves — what `Build.setItems` does on every other
    // path. This one never reaches it: it builds the Build from the payload
    // directly, so the drop has to happen here.
    const weaponReplaced = weaponIdOf(solverBuildPayload.items) !== weaponIdOf(baseBuild?.items);

    return new Build({
        lvl: baseBuild?.lvl || DEFAULT_LVL,
        items: solverBuildPayload.items,
        setBonuses: solverBuildPayload.set_bonuses,
        attributes,
        hasScrolls: payloadHasScrolls ?? true,
        scrolls,
        desired: baseBuild?.desired || {},
        desiredVersion: baseBuild?.desiredVersion,
        maximizeTargets: baseBuild?.maximizeTargets || {},
        maximizeSpellIds: baseBuild?.maximizeSpellIds || [],
        maximizeSpellCriteria: baseBuild?.maximizeSpellCriteria || [],
        maximizeWeapon: baseBuild?.maximizeWeapon || false,
        // Kept through a solve: the run just filled the weapon slot, and losing
        // the criterion here would turn the next generation into "optimise what
        // you found" without the user asking for that.
        maximizeBestWeapon: baseBuild?.maximizeBestWeapon || false,
        bestWeaponMetric: baseBuild?.bestWeaponMetric,
        // Presence, not truthiness: a solve that chose the weapon answers with
        // an element *or* with null for "as forged", and both are its answer.
        // Reading `null` as "said nothing" left the previous element in place
        // on every run that decided against maging.
        weaponMagingElement: 'weapon_maging_element' in solverBuildPayload
            ? solverBuildPayload.weapon_maging_element
            : (weaponReplaced ? null : (baseBuild?.weaponMagingElement ?? null)),
        // The heal half has no solver answer to read: nothing in the search
        // chooses it, so it is the player's and survives the run — but only as
        // long as the weapon does. Carried onto a weapon the solver just put in
        // the slot it becomes an element nothing on screen can show (the new
        // weapon need have no heal row at all) while it still shapes
        // `gen/authorize` and the weapon score — and it surfaces on the slot's
        // maging trigger the moment the damage half is cleared.
        weaponMagingHealElement: weaponReplaced
            ? null
            : (baseBuild?.weaponMagingHealElement ?? null),
        // Carried whole: Build keeps only the slots whose item the run left in
        // place, so a replaced item sheds its overmage and exo lines there.
        // The run then writes the transcendence runes it chose and the exos it
        // placed.
        itemMaging: withSolverMaging(
            baseBuild?.itemMaging ?? {},
            solverBuildPayload.items,
            solverBuildPayload.maging,
        ),
        transcendence: baseBuild?.transcendence ?? null,
        fmExos: baseBuild?.fmExos,
        // A boost is the player's own assumption about the fight, not something
        // the solver has an opinion on — it survives a generation untouched.
        boosts: baseBuild?.boosts ?? {},
        characterClass: baseBuild?.getCharacterClass?.() ?? baseBuild?.characterClass,
        characterSex: baseBuild?.getCharacterSex?.() ?? baseBuild?.characterSex ?? 0,
        criteriaTags: baseBuild?.criteriaTags ?? [],
        criteriaWeights: baseBuild?.criteriaWeights ?? {},
    });
}

